import { badgeVariants } from "@/core/components/ui/badge";
import {
  Tabs,
  TabsListWithIndicator,
  TabsTrigger,
} from "@/core/components/ui/tabs";
import { cn } from "@/core/utils/utils";
import {
  ST_ADJUSTMENTS_ACCESS,
  type StAdjustmentsAccess,
} from "./st-adjustments/types";
import {
  PAYROLL_EMAILS_ACCESS,
  PAY_BREAKDOWN_SECTION,
  type PayBreakdownSection,
  type PayrollEmailsAccess,
} from "./types";

const newTabBadgeClassName = cn(
  badgeVariants({ variant: "orange" }),
  "absolute right-0 top-0 bg-orange-50 px-1 py-0 text-[9px] leading-3 text-primary-foreground",
);

interface PayBreakdownSectionTabsProps {
  activeSection: PayBreakdownSection;
  showRoster: boolean;
  showPayPlan: boolean;
  payrollEmailsAccess: PayrollEmailsAccess;
  /** Open message threads in this period, counted on PackChat. */
  openThreadCount?: number;
  showPackChat?: boolean;
  /** PushPay access controls whether the tab is hidden, live, or a preview. */
  stAdjustmentsAccess: StAdjustmentsAccess;
  onSectionChange: (section: PayBreakdownSection) => void;
}

export const PayBreakdownSectionTabs = ({
  activeSection,
  showRoster,
  showPayPlan,
  payrollEmailsAccess,
  openThreadCount = 0,
  showPackChat = false,
  stAdjustmentsAccess,
  onSectionChange,
}: PayBreakdownSectionTabsProps) => (
  <Tabs
    value={activeSection}
    onValueChange={(value) => onSectionChange(value as PayBreakdownSection)}
    className="w-full"
  >
    <TabsListWithIndicator
      size="small"
      containerClassName="w-full"
      className="justify-start"
    >
      <TabsTrigger value={PAY_BREAKDOWN_SECTION.breakdown}>Grid</TabsTrigger>
      {showRoster && (
        <TabsTrigger value={PAY_BREAKDOWN_SECTION.roster}>Roster</TabsTrigger>
      )}
      {showPayPlan && (
        <TabsTrigger value={PAY_BREAKDOWN_SECTION.payPlan}>Plan</TabsTrigger>
      )}
      {stAdjustmentsAccess !== ST_ADJUSTMENTS_ACCESS.hidden && (
        <TabsTrigger
          value={PAY_BREAKDOWN_SECTION.adjustments}
          aria-label={
            stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview
              ? "PushPay, new"
              : undefined
          }
          className={cn({
            relative: stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview,
          })}
        >
          <span>PushPay</span>
          {stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview && (
            <span className={newTabBadgeClassName} aria-hidden>
              New
            </span>
          )}
        </TabsTrigger>
      )}
      {payrollEmailsAccess !== PAYROLL_EMAILS_ACCESS.hidden && (
        <TabsTrigger
          value={PAY_BREAKDOWN_SECTION.packs}
          aria-label={
            payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.preview
              ? "PayPacks, new"
              : undefined
          }
          className="relative"
        >
          <span>PayPacks</span>
          {payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.preview && (
            <span className={newTabBadgeClassName} aria-hidden>
              new
            </span>
          )}
        </TabsTrigger>
      )}
      {showPackChat && (
        <TabsTrigger value={PAY_BREAKDOWN_SECTION.packChat}>
          <span className="flex items-center gap-1.5">
            PackChat
            {/* The count is the point of the tab: without it a manager has to
                open it to find out whether anyone is waiting. */}
            {openThreadCount > 0 && (
              <span
                className="rounded-full bg-gold-20 px-1.5 text-[11px] font-semibold leading-[18px] text-primary-text"
                aria-label={`${openThreadCount} open ${
                  openThreadCount === 1 ? "thread" : "threads"
                }`}
              >
                {openThreadCount}
              </span>
            )}
          </span>
        </TabsTrigger>
      )}
    </TabsListWithIndicator>
  </Tabs>
);
